import { useEffect, useState } from "react";
import { getConsent, setConsent } from "@/lib/consent";
import { trackPageview, initScrollTracking, initDwellTracking } from "@/lib/track";

const activateAnalytics = () => {
  initScrollTracking();
  initDwellTracking();
  trackPageview();
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsChoice, setAnalyticsChoice] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    } else if (existing.analytics) {
      // Returning visitor who already consented: (re)attach scroll/dwell
      // listeners only. The pageview itself is already covered by
      // AnalyticsTracker's own mount-time effect in App.js.
      initScrollTracking();
      initDwellTracking();
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent(true);
    setVisible(false);
    activateAnalytics();
  };

  const rejectAll = () => {
    setConsent(false);
    setVisible(false);
  };

  const saveSelection = () => {
    setConsent(analyticsChoice);
    setVisible(false);
    if (analyticsChoice) activateAnalytics();
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] bg-[#16273F] text-white shadow-2xl"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
        <p className="text-sm text-slate-200 leading-relaxed">
          Wir verwenden ein Cookie, um mit Ihrer Zustimmung anonyme
          Nutzungsstatistiken zu unserer eigenen, cookie-freien
          Reichweitenmessung zu speichern (kein Google Analytics, kein Meta
          Pixel, keine Weitergabe an Dritte). Mehr dazu in unserer{" "}
          <a href="/datenschutz" className="underline hover:text-white">
            Datenschutzerklärung
          </a>
          .
        </p>

        {settingsOpen && (
          <div className="mt-4 flex items-center justify-between gap-4 bg-white/5 rounded-lg px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Analyse-Cookie</p>
              <p className="text-xs text-slate-300">Anonyme Reichweitenmessung (Seitenaufrufe, Scroll-Tiefe, Formular-Funnel)</p>
            </div>
            <button
              type="button"
              data-testid="cookie-analytics-toggle"
              onClick={() => setAnalyticsChoice((v) => !v)}
              aria-pressed={analyticsChoice}
              className={`relative h-7 w-12 rounded-full transition-colors shrink-0 ${analyticsChoice ? "bg-[#9A8420]" : "bg-white/20"}`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-transform ${analyticsChoice ? "translate-x-5" : "translate-x-0.5"}`}
              />
            </button>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          {!settingsOpen ? (
            <>
              <button
                type="button"
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="h-10 px-5 rounded-full border border-white/30 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Nur notwendige
              </button>
              <button
                type="button"
                data-testid="cookie-open-settings"
                onClick={() => setSettingsOpen(true)}
                className="h-10 px-5 rounded-full border border-white/30 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Einstellungen
              </button>
              <button
                type="button"
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="h-10 px-5 rounded-full bg-[#9A8420] hover:bg-[#7d6b1a] text-sm font-semibold transition-colors"
              >
                Alle akzeptieren
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="cookie-save-selection"
              onClick={saveSelection}
              className="h-10 px-5 rounded-full bg-[#9A8420] hover:bg-[#7d6b1a] text-sm font-semibold transition-colors"
            >
              Auswahl speichern
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
